import { ButtonDestructive, ButtonSecondary, ButtonTertiary } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { MissionTableItem } from "@nova/schemas"
import { useMemo } from "react"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectSelectedMissionIds } from "../redux/missions-table-selectors"
import { clearSelectedMissionIds } from "../redux/missions-table-slice"
import { setGlobalNotificationContext } from "user/profile/slice"
import { useConfirmationModalContext } from "features/modals/WorkspaceConfirmationModalV2"
import {
    LinkLayersModal,
    useHandleLinkLayersBulkSubmission,
} from "../missions/linked-layers/LinkLayersModal"
import { getCompleteChecklistModalComponent } from "./CompleteChecklistModal"
import { MoreActionsDropdown } from "./MoreActionsDropdown"
import { useBulkDeleteMissions, useCreateMissionGroup } from "./PilotTrackingMissionQueries"
import { AssignDroneDropdown } from "./AssignDroneDropdown"
import { AssignPilotDropdown } from "./AssignPilotDropdown"
import { EditFlightDetailsModal, countSyncedMissions } from "./EditFlightDetailsModal"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { pluralize } from "@nova/util-strings"

const getDeleteFlightsModal = (count: number) => {
    const DeleteFlightsModal = ({
        confirm,
        cancel,
        isPerformingAsyncConfirmCallback,
    }: {
        confirm: () => void
        cancel: () => void
        isPerformingAsyncConfirmCallback: boolean
    }) => (
        <HighPriorityNovaModalContainer
            title={`Delete ${pluralize({ count, str: "Flight" })}?`}
            closeModal={cancel}
            className="w-full max-w-[500px]"
            bottomBar={
                <div className="flex gap-2 justify-end w-full">
                    <ButtonTertiary title="Cancel" onClick={cancel} />
                    <ButtonDestructive
                        title={`Delete ${pluralize({ count, str: "Flight" })}`}
                        LeftIcon={Icons.Trash}
                        disabled={isPerformingAsyncConfirmCallback}
                        onClick={confirm}
                    />
                </div>
            }
        >
            <div className="flex flex-col items-center gap-3 py-4 text-center">
                <div className="text-base text-white">
                    Deleting flights removes all data and metrics from tracking, do you want to
                    proceed?
                </div>
                <p className="text-base text-red-500">This cannot be undone.</p>
            </div>
        </HighPriorityNovaModalContainer>
    )
    return DeleteFlightsModal
}

export const MissionsTableActionBar = ({
    data,
    onMutate,
}: {
    data: MissionTableItem[]
    onMutate: () => void
}) => {
    const dispatch = useAppDispatch()
    const modal = useConfirmationModalContext()
    const { isEditor } = useWorkspacePermissions()
    const selectedMissionIds = useAppSelector(selectSelectedMissionIds)
    const numSelectedMissions = Object.keys(selectedMissionIds).length
    const selectedMissionIdList = Object.keys(selectedMissionIds)
    const handleLinkLayersBulk = useHandleLinkLayersBulkSubmission(selectedMissionIdList)
    const { bulkDeleteMissions } = useBulkDeleteMissions(selectedMissionIdList)
    const { createGroup, isCreating } = useCreateMissionGroup()

    const selectedData = data
        .flatMap((d) => (d.type === "mission" ? d : (d.missions ?? [])))
        .filter((d) => Object.keys(selectedMissionIds).includes(d.id))

    const allSelectedDataHasSamePilot = selectedData.every(
        (d) => !!d.user_id && d.user_id === selectedData[0]?.user_id
    )
    const allSelectedDataHasSameDrone = selectedData.every(
        (d) => !!d.aircraft?.id && d.aircraft.id === selectedData[0]?.aircraft?.id
    )

    const anySelectedInGroup = useMemo(
        () =>
            selectedMissionIdList.some((id) =>
                data.some((d) => d.type === "group" && d.missions?.some((m) => m.id === id))
            ),
        [selectedMissionIdList, data]
    )

    const bulkDelete = async () => {
        const deleteCount = numSelectedMissions
        try {
            await bulkDeleteMissions()
            dispatch(clearSelectedMissionIds())
            dispatch(
                setGlobalNotificationContext({
                    message: `${pluralize({ count: deleteCount, single: "flight has", plural: "flights have" })} been deleted.`,
                    status: GlobalNotificationStatus.success,
                    timeoutInMs: 3000,
                })
            )
        } catch {
            dispatch(
                setGlobalNotificationContext({
                    message: `Failed to delete ${pluralize({ count: deleteCount, str: "flight" })}. Please try again.`,
                    status: GlobalNotificationStatus.error,
                    timeoutInMs: 5000,
                })
            )
        }
    }

    return (
        <div className="flex items-center justify-between gap-2 w-full py-px">
            <div className="flex items-center gap-2 shrink-0">
                <div
                    className="text-gray-300 hover:text-white transition-colors cursor-pointer"
                    onClick={() => dispatch(clearSelectedMissionIds())}
                >
                    <Icons.SmallIcon Icon={Icons.XClose} />
                </div>
                <h5 className="text-white">{numSelectedMissions} selected</h5>
            </div>
            <div className="flex items-center gap-1 justify-end flex-wrap">
                <ButtonTertiary
                    title="Complete Checklist"
                    LeftIcon={Icons.Docs}
                    onClick={() => {
                        void modal.confirm({
                            RenderComponent: getCompleteChecklistModalComponent({
                                missionIds: selectedMissionIdList,
                            }),
                        })
                    }}
                />
                {isEditor && (
                    <>
                        {!anySelectedInGroup && numSelectedMissions > 1 && (
                            <ButtonSecondary
                                title="Group"
                                onClick={async () => {
                                    await createGroup(Object.keys(selectedMissionIds))
                                    onMutate()
                                }}
                                disabled={isCreating}
                            />
                        )}
                        <ButtonSecondary
                            title="Link Layers"
                            onClick={() => {
                                void modal.confirm<{ projectId: string; layerIds: string[] }>({
                                    RenderComponent: LinkLayersModal,
                                    asyncConfirmCallback: handleLinkLayersBulk,
                                })
                            }}
                        />
                        <AssignPilotDropdown
                            missionIds={selectedMissionIdList}
                            hasMixedPilotAssignment={
                                selectedData.some((d) => !!d.pilot) && !allSelectedDataHasSamePilot
                            }
                            defaultCheckedPilotId={
                                allSelectedDataHasSamePilot ? selectedData[0]?.user_id : undefined
                            }
                            position="bottom-right"
                        />
                        <AssignDroneDropdown
                            missionIds={selectedMissionIdList}
                            hasMixedDroneAssignment={
                                selectedData.some((d) => !!d.aircraft) &&
                                !allSelectedDataHasSameDrone
                            }
                            defaultCheckedDroneId={
                                allSelectedDataHasSameDrone
                                    ? selectedData[0]?.aircraft?.id
                                    : undefined
                            }
                            position="bottom-right"
                        />
                        <ButtonSecondary
                            title="Edit Details"
                            onClick={() => {
                                void modal.confirm({
                                    RenderComponent: ({ confirm, cancel }) => (
                                        <EditFlightDetailsModal
                                            missionIds={selectedMissionIdList}
                                            syncedCount={countSyncedMissions(selectedData)}
                                            onConfirm={() => {
                                                dispatch(clearSelectedMissionIds())
                                                confirm()
                                            }}
                                            onCancel={cancel}
                                        />
                                    ),
                                })
                            }}
                        />
                        <MoreActionsDropdown
                            missions={selectedData.map((d) => ({
                                missionId: d.id,
                                tagIds: d.tags?.map<string>((t) => t.id) ?? [],
                            }))}
                            onSubmit={onMutate}
                        />
                        <ButtonDestructive
                            title="Delete"
                            LeftIcon={Icons.Trash}
                            onClick={() => {
                                void modal.confirm({
                                    RenderComponent: getDeleteFlightsModal(numSelectedMissions),
                                    asyncConfirmCallback: bulkDelete,
                                })
                            }}
                        />
                    </>
                )}
            </div>
        </div>
    )
}
